import React, { useEffect } from 'react';
import { useLoaderData, useParams } from '@tanstack/react-router';
import { CurrentPageChangingEvent, SurveyModel } from 'survey-core';
import {
  UserType, SIGNED_SUBMISSION_STATUSES,
} from '../../types';
import { useStatusMessageDispatch } from '../../hooks/StatusMessageContext';
import { patchSubmission } from '../../api/submissions/patch-submission';
import { filterEncryptedInputValues, commitEncryptedInputEdits } from '../survey-questions/encrypted-input';
import { getSurveyModel } from '../survey-questions/get-survey-model';
import Survey from '../survey/survey';
import { signSubmission } from '../../api/submissions/sign-submission';
import { SubmissionLoaderResponse } from '../../utils/submission-route-loader';
import {
  isApiError, isUnauthorizedError,
  isExpiredToken,
  isNotFoundError,
  isBadRequestError,
} from '../../utils/errors';
import { displayCustomPage, IS_CUSTOM_PAGE } from '../../utils/custom-page/display-custom-page';
import { TokenExpirationMessage } from '../token-expiration-message/token-expiration-message';
import ErrorPage from '../status-pages/error-page';
import i18n from '../../i18n';
import { setDatadogUser } from '../../utils/datadog-user-tracking';

const SurveySigner: React.FC = () => {
  const {
    submission,
    settings,
    error,
  } = useLoaderData({ from: '/$accountSlug/$submissionId/signer' }) as SubmissionLoaderResponse;
  const lockSubmission = !submission || !!error || SIGNED_SUBMISSION_STATUSES.has(submission.status);
  const { submissionId } = useParams({ from: '/$accountSlug/$submissionId/signer' });
  const { setStatusMessage } = useStatusMessageDispatch();

  // Track user in Datadog when submission is loaded
  useEffect(() => {
    if (submission) {
      setDatadogUser(submission);
    }
  }, [ submission ]);

  // Early return for not found error - no need to create survey model
  if (isNotFoundError(error) || isBadRequestError(error)) {
    return <ErrorPage
      errorType={error}
      link={`/${settings.urlSlug}/`}
           />;
  }

  const handleSubmission = async (sender: SurveyModel) => {
    if (lockSubmission) return;

    try {
      await signSubmission(
        settings.urlSlug,
        submission.publicId,
        filterEncryptedInputValues(sender.data, sender),
        UserType.SIGNER,
      );
    } catch (err) {
      setStatusMessage({
        severity: 'error',
        message: isApiError(err) ? err.message : 'Failed to submit application.',
      });
    }
  };

  const handleSave = async (sender: SurveyModel, options: CurrentPageChangingEvent) => {
    if (lockSubmission) return;
    // Do not save invalid data
    if (!options.oldCurrentPage?.validate(false)) return; // Allows preview page to be used (`showPreviewBeforeComplete=true`)
    // if custom page is added do not execute after triggering the CurrentPageChangingEvent
    if (options.newCurrentPage.getPropertyValue(IS_CUSTOM_PAGE)) return;

    try {
      await patchSubmission(settings.urlSlug, submission.publicId, filterEncryptedInputValues(sender.data, sender), UserType.SIGNER);
      commitEncryptedInputEdits(sender);
    } catch (error) {
      if (isApiError(error) && isExpiredToken(error)) {
        displayCustomPage({
          survey: sender,
          component: TokenExpirationMessage,
          getProps: () => ({
            publicId: submissionId,
            slug: settings.urlSlug,
          }),
          withoutFrame: true,
        });

        sender.readOnly = true;
      } else {
        setStatusMessage({
          severity: 'error',
          message: isApiError(error) ? error.message : 'Failed to save.',
        });
      }
    }
  };

  const surveyModel = getSurveyModel({
    settings,
    submission,
    readOnly: lockSubmission,
    onComplete: handleSubmission,
    onCurrentPageChanging: handleSave,
    completedHtml: i18n.t('components:pages.application_submitted'),
    currentPage: 1,
    isSigner: true,
  });

  // Handle token expiration - signer can request a new invitation
  if (isExpiredToken(error)) {
    displayCustomPage({
      survey: surveyModel,
      component: TokenExpirationMessage,
      getProps: () => ({
        publicId: submissionId,
        slug: settings.urlSlug,
      }),
      withoutFrame: true,
    });

    // Remove all pages except custom page
    const customPage = surveyModel.getPageByName(TokenExpirationMessage.name);
    surveyModel.pages.slice().forEach((page) => {
      if (page !== customPage) {
        surveyModel.removePage(page);
      }
    });
    surveyModel.showProgressBar = 'off';
    surveyModel.showTOC = false;
    surveyModel.showNavigationButtons = false;
  }

  // Handle other unauthorized errors - signer needs valid invitation link
  if (isUnauthorizedError(error)) {
    return <ErrorPage
      errorType={error}
      link={`/${settings.urlSlug}/`}
           />;
  }

  return (
    <Survey
      surveyModel={surveyModel}
    />
  );
};

export default SurveySigner;
